import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { QrCode, Image as ImageIcon, Download, LogOut } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

export default function UserPortal() {
  const [email, setEmail] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [showImages, setShowImages] = useState(false);
  const [templateImages, setTemplateImages] = useState([]);

  useEffect(() => {
    // Check if user is already logged in
    const savedEmail = localStorage.getItem("userPortalEmail");
    if (savedEmail) {
      setUserEmail(savedEmail);
      setIsLoggedIn(true);
      fetchAccessibleTemplates(savedEmail);
    }
  }, []);

  const handleLogin = async () => {
    if (!email.trim()) {
      alert("Please enter your email");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API}/templates/accessible/${email.toLowerCase().trim()}`);
      if (response.data.length === 0) {
        alert("No templates found for this email. Please check with the admin who shared access with you.");
        setLoading(false);
        return;
      }
      
      localStorage.setItem("userPortalEmail", email.toLowerCase().trim());
      setUserEmail(email.toLowerCase().trim());
      setTemplates(response.data);
      setIsLoggedIn(true);
    } catch (error) {
      alert("Failed to login. Please try again.");
    }
    setLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem("userPortalEmail");
    setIsLoggedIn(false);
    setUserEmail("");
    setTemplates([]);
    setEmail("");
  };

  const fetchAccessibleTemplates = async (emailAddr) => {
    try {
      const response = await axios.get(`${API}/templates/accessible/${emailAddr}`);
      setTemplates(response.data);
    } catch (error) {
      console.error("Failed to fetch templates");
    }
  };

  const viewQRCode = (template) => {
    setSelectedTemplate(template);
    setQrCodeUrl(`${API}/templates/${template.id}/qr`);
    setShowQR(true);
  };

  const viewImages = async (template) => {
    setSelectedTemplate(template);
    try {
      const response = await axios.get(`${API}/templates/${template.id}/images`);
      setTemplateImages(response.data);
      setShowImages(true);
    } catch (error) {
      alert("Failed to load images");
    }
  };

  const downloadPDF = (templateId) => {
    window.open(`${API}/templates/${templateId}/pdf`, '_blank');
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-0 shadow-xl">
          <CardHeader className="text-center">
            <CardTitle className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
              User Portal
            </CardTitle>
            <CardDescription>
              Enter your email to access shared templates
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Input
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                className="text-lg py-6"
              />
            </div>
            <Button 
              onClick={handleLogin} 
              disabled={loading}
              className="w-full py-6 text-lg bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600"
            >
              {loading ? "Checking..." : "Access My Templates"}
            </Button>
            <p className="text-sm text-gray-500 text-center">
              Contact your admin if you don't have access yet
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
              My Templates
            </h1>
            <p className="text-gray-600">Logged in as: {userEmail}</p>
          </div>
          <Button variant="outline" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>

        {/* Templates Grid */}
        {templates.length === 0 ? (
          <Card className="border-0 shadow-xl">
            <CardContent className="text-center py-12">
              <p className="text-gray-500 text-lg">No templates shared with you yet.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.map((template) => (
              <Card 
                key={template.id} 
                className="border-0 shadow-xl bg-white/90 hover:shadow-2xl transition-all"
              >
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    {template.template_type === "branded" ? "🏷️" : template.template_type === "single" ? "📄" : template.template_type === "event" ? "🎉" : "⭕"}
                    {template.name}
                  </CardTitle>
                  <CardDescription>
                    {template.template_type === "branded" 
                      ? `Branded Card 2.25"×3.25" • ${template.filled_count} photos`
                      : template.template_type === "single"
                      ? `Single Photo • ${template.filled_count} photos`
                      : template.template_type === "event"
                      ? `Event Gallery • ${template.filled_count} photos`
                      : `${template.filled_count} / ${template.total_slots} filled`
                    }
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Progress */}
                  <div className="bg-gradient-to-r from-blue-100 to-purple-100 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-gray-700">Photos</span>
                      <span className="text-sm font-bold text-purple-600">
                        {template.filled_count}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full"
                        style={{ width: `${Math.min((template.filled_count / Math.max(template.total_slots, 1)) * 100, 100)}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="grid grid-cols-2 gap-2">
                    <Button 
                      variant="outline" 
                      onClick={() => viewQRCode(template)}
                      className="w-full"
                    >
                      <QrCode className="w-4 h-4 mr-2" />
                      QR Code
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => viewImages(template)}
                      className="w-full"
                    >
                      <ImageIcon className="w-4 h-4 mr-2" />
                      Images ({template.filled_count})
                    </Button>
                  </div>
                  
                  <Button 
                    onClick={() => downloadPDF(template.id)}
                    className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* QR Code Dialog */}
        <Dialog open={showQR} onOpenChange={setShowQR}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{selectedTemplate?.name} - QR Code</DialogTitle>
              <DialogDescription>
                Share this QR code for people to upload photos
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col items-center gap-4">
              <img 
                src={qrCodeUrl} 
                alt="QR Code" 
                className="w-64 h-64 border rounded-lg"
              />
              <p className="text-sm text-gray-500 text-center">
                Scan this code to upload photos to "{selectedTemplate?.name}"
              </p>
              <Button 
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = qrCodeUrl;
                  link.download = `${selectedTemplate?.name}-qr.png`;
                  link.click();
                }}
                className="w-full"
              >
                <Download className="w-4 h-4 mr-2" />
                Download QR Code
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Images Dialog */}
        <Dialog open={showImages} onOpenChange={setShowImages}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedTemplate?.name} - Images ({templateImages.length})</DialogTitle>
              <DialogDescription>
                Click on an image to download
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-4 p-4">
              {templateImages.length === 0 ? (
                <p className="col-span-full text-center text-gray-500 py-8">
                  No images uploaded yet
                </p>
              ) : (
                templateImages.map((img) => (
                  <div key={img.id} className="relative group">
                    <div className="aspect-[2.25/3.25] rounded-lg overflow-hidden border-2 border-gray-200 group-hover:border-purple-500 transition-all">
                      <img
                        src={`${API}/images/${img.id}`}
                        alt={`Photo ${img.circle_index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {img.user_name && (
                      <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white px-2 py-1 text-xs truncate">
                        {img.user_name}
                      </div>
                    )}
                    <a
                      href={`${API}/images/${img.id}/download`}
                      download
                      className="absolute top-2 right-2 bg-blue-500 hover:bg-blue-600 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Download className="w-4 h-4" />
                    </a>
                  </div>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
